import React, { useEffect } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import {
  useTable,
  useRowSelect,
  usePagination,
  useExpanded,
  useGlobalFilter,
} from "react-table";
import {
  Button,
  FormHelperText,
  MenuItem,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import { Menu } from "@material-ui/icons";
import { Link } from "react-router-dom";
import Grid from "@material-ui/core/Grid";
import { useForm } from "react-hook-form";
import { ToggleButton, ToggleButtonGroup } from "@material-ui/lab";

import apiHelper from "../../../../util/ApiHelper";
import { useLoading } from "../../../../util/LoadingProvider";
import { toast } from "react-toastify";
import { CustomCheckbox, modal } from "../../../../util";

const useStyles = makeStyles((theme) => ({
  input: {
    width: "100%",
  },
  margin: {
    marginBottom: "40px",
  },
  flexRow: {
    display: "flex",
    justifyContent: "space-between",
  },
  pagination: {
    "& .MuiTextField-root": {
      width: "35px",
      minWidth: "auto",
    },
    "& input": {
      padding: "4px 0",
      marginLeft: "10px",
      marginRight: "10px",
    },
    "& button": {
      background: "white",
      color: "#cccccc",
      border: "1px solid #CCCCCC",
      minWidth: "auto",
      width: "24px",
      height: "24px",
      margin: "4px 0",
      "&.left": {
        borderRadius: "4px 0 0 4px",
        marginLeft: "10px",
      },
      "&.right": {
        borderRadius: " 0 4px 4px 0",
      },
    },
  },
}));

function formatData(content) {
  const formattedData = [];

  content.forEach((row) => {
    formattedData.push({
      id: row.id,
      enName: row.localizations.en.name,
      tcName: row.localizations.tc.name,
      slug: row.slug,
      count: row.count,
      parentId: row.parentId,
      subRows: formatSubRows(row.childPeriod),
      sequence: row.sequence,
    });
  });

  return formattedData;
}

function sortingData(data) {
  return data.sort((a, b) => a.sequence - b.sequence);
}

export function formatSubRows(content) {
  const result = formatData(content);
  const sorted = sortingData(result);
  return sorted;
}

const IsDraggingContext = React.createContext(false);

const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

class DraggableTableCell extends React.Component {
  getSnapshotBeforeUpdate(prevProps) {
    if (!this.ref) {
      return null;
    }

    const isDragStarting =
      this.props.isDragOccurring && !prevProps.isDragOccurring;

    if (!isDragStarting) {
      return null;
    }

    const { width, height } = this.ref.getBoundingClientRect();

    const snapshot = {
      width,
      height,
    };

    return snapshot;
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    const ref = this.ref;
    if (!ref) {
      return;
    }

    if (snapshot) {
      if (ref.style.width === snapshot.width) {
        return;
      }
      ref.style.width = `${snapshot.width}px`;
      ref.style.height = `${snapshot.height}px`;
      return;
    }

    if (this.props.isDragOccurring) {
      return;
    }

    // inline styles not applied
    if (ref.style.width == null) {
      return;
    }

    // no snapshot and drag is finished - clear the inline styles
    ref.style.removeProperty("height");
    ref.style.removeProperty("width");
  }

  setRef = (ref) => {
    this.ref = ref;
  };

  render() {
    return (
      <TableCell
        style={{ paddingTop: 6, paddingBottom: 6, color: "#306F9D" }}
        ref={this.setRef}
        align={this.props.align}
      >
        {this.props.children}
      </TableCell>
    );
  }
}

const IndeterminateCheckbox = React.forwardRef(
  ({ indeterminate, ...rest }, ref) => {
    const defaultRef = React.useRef();
    const resolvedRef = ref || defaultRef;

    React.useEffect(() => {
      resolvedRef.current.indeterminate = indeterminate;
    }, [resolvedRef, indeterminate]);

    return <CustomCheckbox color="primary" ref={resolvedRef} {...rest} />;
  }
);

const DraggableTableRow = (props) => {
  const {
    snapshot,
    row,
    provided,
    className,
    getToggleRowSelectedProps,
    disabled,
  } = props;
  return (
    <IsDraggingContext.Consumer>
      {(isDragging) => (
        <TableRow
          className={className}
          ref={provided.innerRef}
          isDragging={snapshot.isDragging}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
        >
          <DraggableTableCell isDragOccurring={isDragging} align="left">
            <IndeterminateCheckbox
              {...{ ...getToggleRowSelectedProps, disabled }}
            />
          </DraggableTableCell>
          <DraggableTableCell
            isDragOccurring={isDragging}
            component="th"
            scope="row"
          >
            <Link
              to={`/hknt/period/edit/${row.id}`}
              style={{ color: "#306F9D", textDecoration: "none" }}
            >
              {row.enName}
            </Link>
          </DraggableTableCell>
          <DraggableTableCell isDragOccurring={isDragging} align="left">
            <div>{row.tcName}</div>
          </DraggableTableCell>
          <DraggableTableCell isDragOccurring={isDragging} align="left">
            <span style={{ color: "#333333" }}>{row.slug}</span>
          </DraggableTableCell>
          <DraggableTableCell isDragOccurring={isDragging} align="left">
            <span style={{ color: "#333333" }}>{row.count}</span>
          </DraggableTableCell>
          <DraggableTableCell isDragOccurring={isDragging} align="right">
            <Menu fontSize="small" style={{ color: "#CCCCCC" }} />
          </DraggableTableCell>
        </TableRow>
      )}
    </IsDraggingContext.Consumer>
  );
};

// Define a default UI for filtering
function GlobalFilter({ preGlobalFilteredRows, globalFilter, setData }) {
  const [value, setValue] = React.useState(globalFilter);

  const onChange = () => {
    apiHelper.hknt.hkntGetPeriodList(value ? { keyword: value } : { unpaged: true })
      .then((response) => {
        const unsorted = formatData(response.data.content);
        const sorted = sortingData(unsorted);
        setData(sorted);
      });
  };

  return (
    <>
      <TextField
        id="search-field"
        label="Search Periods"
        variant="outlined"
        margin="dense"
        value={value || ""}
        onChange={(e) => {
          setValue(e.target.value);
        }}
      />
      <Button
        type="submit"
        variant="outlined"
        color="primary"
        onClick={() => {
          onChange(value);
        }}
      >
        Search
      </Button>
    </>
  );
}

export default function PeriodListingPage({ disabled, ...props }) {
  const classes = useStyles();
  const [isDragging, setIsDragging] = React.useState(false);
  const [parentPeriod, setParentPeriod] = React.useState(null);
  const [periodStatus, setPeriodStatus] = React.useState("active");
  const [bulkActions, setBulkActions] = React.useState("");
  const [parents, setParents] = React.useState([]);
  // const [loading, setLoading] = React.useState(false);
  const { setLoading } = useLoading();

  const actions = [
    {
      value: "delete",
      label: "Delete",
    },
  ];

  const handleChange = (event) => {
    setParentPeriod(event.target.value);
  };

  const handleStatus = (event, newAlignment) => {
    if (newAlignment) setPeriodStatus(newAlignment);
  };

  const handleBulkActions = (event, actions) => {
    setBulkActions(event.target.value);
  };

  const [data, setData] = React.useState([]);

  const getPeriodList = async () => {
    setLoading(true);
    const result = await apiHelper.hknt.hkntGetPeriodList({ unpaged: true });
    const unsorted = formatData(result.data.content);
    const sorted = sortingData(unsorted);
    setData(sorted);
    setLoading(false);
  };

  const getParentPeriodList = async () => {
    setLoading(true);
    const result = await apiHelper.hknt.hkntGetParentPeriodList();
    const periods = result.data.sort((a, b) => a.sequence - b.sequence);
    setParents(periods);
    setLoading(false);
  };

  const refetchData = async () => {
    try {
      await getPeriodList();
      await getParentPeriodList();
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const defaultExpandedRows = [];
  for (let index = 0; index < 10; index++) {
    defaultExpandedRows.push({ index: true });
  }

  useEffect(() => {
    getPeriodList();
    getParentPeriodList();
  }, []);

  const columns = React.useMemo(
    () => [
      {
        Header: "Name (English)",
        accessor: "enName",
      },
      {
        Header: "Name (Traditional)",
        accessor: "tcName",
      },
      {
        Header: "Slug",
        accessor: "slug",
      },
      {
        Header: "Count",
        accessor: "count",
      },
      {
        Header: "",
        accessor: "order",
      },
    ],
    [data]
  );

  const {
    getTableProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    gotoPage,
    nextPage,
    previousPage,
    preGlobalFilteredRows,
    selectedFlatRows,
    toggleRowExpanded,
    state: { globalFilter },
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, expanded: defaultExpandedRows },
      expandSubRows: false,
    },
    useGlobalFilter,
    useExpanded,
    usePagination,
    useRowSelect,
    (hooks) => {
      hooks.visibleColumns.push((columns) => [
        {
          id: "selection",
          Header: ({ getToggleAllRowsSelectedProps }) => (
            <div>
              <IndeterminateCheckbox
                {...{ ...getToggleAllRowsSelectedProps(), disabled }}
              />
            </div>
          ),
        },
        ...columns,
      ]);
    }
  );

  const onBeforeCapture = (beforeCapture) => {
    toggleRowExpanded(beforeCapture.draggableId, false);
  };

  const onBeforeDragStart = () => {
    setIsDragging(true);
  };

  const onDragEnd = (result) => {
    // dropped outside the list
    if (!result.destination) {
      return;
    }

    const items = reorder(data, result.source.index, result.destination.index);

    setData(items);
    toggleRowExpanded(result.destination.index, true);
  };

  const updatePeriodOrder = async () => {
    try {
      const periodSequences = [];

      data.forEach((row, idx) => {
        periodSequences.push({ id: row.id, sequence: idx + 1 });
      });

      const result = await apiHelper.hknt.hkntUpdatePeriodOrder(periodSequences);
      console.log(result.data);
      toast.dark("Your changes is saved.");
    } catch (err) {
      console.log(err);
    }
  };

  const applyBulkAction = async () => {
    if (bulkActions === "delete") {
      const ids = selectedFlatRows.map((o) => o.original.id);
      if (ids.length > 0) {
        const decision = await modal.fire({
          title: "Delete Period",
          text: `Are you sure to delete ${ids.length} parent period(s)?`,
          showCancelButton: true,
          confirmButtonText: `Delete`,
        });
        if (decision.isConfirmed) {
          try {
            const result = await apiHelper.hknt.hkntBulkDeletePeriod(ids);
            if (result.data.code === 0) {
              toast.dark(`${ids.length} parent period(s) deleted.`);
              getPeriodList();
              getParentPeriodList();
            }
          } catch (err) {
            console.log(err);
          }
        }
      }
    }
  };

  const {
    register,
    handleSubmit,
    reset,
  } = useForm();

  const onSubmit = async (data) => {
    try {
      const params = data;
      params.parentId =
        parentPeriod !== null && parentPeriod !== ""
          ? Number(parentPeriod)
          : null;
      params.archive = periodStatus === "archive" ? 1 : 0;

      const result = await apiHelper.hknt.hkntAddPeriod(params);
      console.log(result.data);
      if (result.data.code === 0) {
        toast.dark("Period is created.");
        setParentPeriod("");
        reset();
        refetchData();
      }
    } catch (err) {
      console.log(err);
    }
  };

  return (
    data && (
      <>
        <IsDraggingContext.Provider value={isDragging}>
          <Typography variant="h4" gutterBottom>
            Programmes & Periods > Periods
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={3} md={4}>
              <Typography variant="h6" gutterBottom>
                Add New Period
              </Typography>

              <form onSubmit={handleSubmit(onSubmit)}>
                <fieldset disabled={disabled} className="fieldset">
                  <TextField
                    id="english-name"
                    label="Name (English)"
                    variant="outlined"
                    className={classes.input}
                    helperText="The name is how it appears on your site."
                    {...register("localizations.en.name")}
                  />
                  <TextField
                    id="chinese-name"
                    label="Name (Traditional Chinese)"
                    variant="outlined"
                    className={classes.input}
                    helperText="The name is how it appears on your site."
                    {...register("localizations.tc.name")}
                  />
                  <TextField
                    id="slug"
                    label="Slug"
                    variant="outlined"
                    className={classes.input}
                    helperText="The “slug” is the URL-friendly version of the name. It is usually all lowercase and contains only letters, numbers, and hyphens."
                    {...register("slug")}
                  />
                  <TextField
                    id="standard-select-parentPeriod"
                    select
                    label="Parent Period"
                    value={parentPeriod}
                    onChange={handleChange}
                    helperText="Assign a parent term to create a hierarchy. The term Jazz, for example, would be the parent of Bebop and Big Band."
                    variant="outlined"
                    className={classes.input}
                  >
                    {parents.map((option) => (
                      <MenuItem key={option.id} value={option.id}>
                        {option.slug}
                      </MenuItem>
                    ))}
                  </TextField>
                  <ToggleButtonGroup
                    value={periodStatus}
                    exclusive
                    onChange={handleStatus}
                    aria-label="period status"
                    className={classes.input}
                  >
                    <ToggleButton
                      value="active"
                      aria-label="active"
                      disabled={disabled}
                    >
                      Active
                    </ToggleButton>
                    <ToggleButton
                      value="archive"
                      aria-label="archive"
                      disabled={disabled}
                    >
                      Archive
                    </ToggleButton>
                  </ToggleButtonGroup>
                  <FormHelperText className={classes.margin}>
                    A status for the period is where it appears on your site.
                  </FormHelperText>
                  {disabled || (
                    <Button type="submit" variant="contained" color="primary">
                      Add New Period
                    </Button>
                  )}
                </fieldset>
              </form>
            </Grid>
            <Grid item xs={9} md={8}>
              <div className={classes.flexRow}>
                <div>
                  <TextField
                    id="bulk-actions-top"
                    select
                    margin="dense"
                    label="Bulk Actions"
                    value={bulkActions}
                    onChange={handleBulkActions}
                    variant="outlined"
                    disabled={disabled}
                  >
                    {actions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <Button
                    type="submit"
                    variant="outlined"
                    color="primary"
                    onClick={applyBulkAction}
                    disabled={disabled}
                  >
                    Apply
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    onClick={updatePeriodOrder}
                    disabled={disabled}
                  >
                    Save Changes
                  </Button>
                </div>
                <div>
                  <GlobalFilter
                    preGlobalFilteredRows={preGlobalFilteredRows}
                    globalFilter={globalFilter}
                    setData={setData}
                  />
                </div>
              </div>

              <Paper>
                {/* {loading ? (
                  "loading..."
                ) : ( */}
                <Table
                  aria-label="simple table"
                  component="ul"
                  {...getTableProps()}
                >
                  <TableHead component="div">
                    {headerGroups.map((headerGroup) => (
                      <TableRow {...headerGroup.getHeaderGroupProps()}>
                        {headerGroup.headers.map((column) => (
                          <TableCell
                            {...column.getHeaderProps()}
                            style={{ paddingTop: 6, paddingBottom: 6 }}
                          >
                            {column.render("Header")}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableHead>
                  <DragDropContext
                    onBeforeDragStart={onBeforeDragStart}
                    onDragEnd={onDragEnd}
                    onBeforeCapture={onBeforeCapture}
                  >
                    <Droppable droppableId="droppable" type="QUESTIONS">
                      {(provided, snapshot) => (
                        <TableBody
                          component="div"
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                        >
                          {page.map((row, index) => {
                            prepareRow(row);
                            const rowProps = row.getRowProps();
                            return (
                              <>
                                <Draggable
                                  key={row.id}
                                  draggableId={row.id}
                                  index={index}
                                  isDragDisabled={disabled}
                                >
                                  {(provided, snapshot) => (
                                    <DraggableTableRow
                                      key={row.id}
                                      row={row.original}
                                      provided={provided}
                                      snapshot={snapshot}
                                      getToggleRowSelectedProps={row.getToggleRowSelectedProps()}
                                      disabled={disabled}
                                    />
                                  )}
                                </Draggable>
                                {row.subRows &&
                                  row.isExpanded &&
                                  row.subRows.map((subRow) => {
                                    const values = subRow.original;
                                    return (
                                      <TableRow {...rowProps}>
                                        <TableCell
                                          style={{
                                            paddingTop: 12,
                                            paddingBottom: 12,
                                          }}
                                        >
                                          {/* <IndeterminateCheckbox
                                          {...row.getToggleRowSelectedProps}
                                        /> */}
                                        </TableCell>
                                        <TableCell
                                          component="th"
                                          style={{
                                            paddingTop: 12,
                                            paddingBottom: 12,
                                          }}
                                        >
                                          <Link
                                            to={`/hknt/period/edit/${values.id}`}
                                            style={{
                                              paddingLeft: "34px",
                                              color: "#306F9D",
                                              textDecoration: "none",
                                            }}
                                          >
                                            {values.enName}
                                          </Link>
                                        </TableCell>
                                        <TableCell
                                          style={{
                                            paddingTop: 12,
                                            paddingBottom: 12,
                                          }}
                                        >
                                          <div
                                            style={{
                                              paddingLeft: "34px",
                                              color: "#306F9D",
                                            }}
                                          >
                                            {values.tcName}
                                          </div>
                                        </TableCell>
                                        <TableCell
                                          style={{
                                            paddingTop: 12,
                                            paddingBottom: 12,
                                          }}
                                        >
                                          {values.slug}
                                        </TableCell>
                                        <TableCell
                                          style={{
                                            paddingTop: 12,
                                            paddingBottom: 12,
                                          }}
                                        >
                                          {values.count}
                                        </TableCell>
                                        <TableCell
                                          style={{
                                            paddingTop: 12,
                                            paddingBottom: 12,
                                          }}
                                        ></TableCell>
                                      </TableRow>
                                    );
                                  })}
                              </>
                            );
                          })}
                          {provided.placeholder}
                        </TableBody>
                      )}
                    </Droppable>
                  </DragDropContext>
                </Table>
                {/* )} */}
              </Paper>

              <div className={classes.flexRow}>
                <div>
                  <TextField
                    id="bulk-actions-bottom"
                    select
                    label="Bulk Actions"
                    margin="dense"
                    value={bulkActions}
                    onChange={handleBulkActions}
                    variant="outlined"
                    disabled={disabled}
                  >
                    {actions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <Button
                    type="submit"
                    variant="outlined"
                    color="primary"
                    onClick={applyBulkAction}
                    disabled={disabled}
                  >
                    Apply
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    onClick={updatePeriodOrder}
                    disabled={disabled}
                  >
                    Save Changes
                  </Button>
                </div>
                <div className={classes.pagination}>
                  Go to{" "}
                  <TextField
                    id="pagination-field"
                    variant="outlined"
                    onChange={(e) => {
                      const page = e.target.value
                        ? Number(e.target.value) - 1
                        : 0;
                      gotoPage(page);
                    }}
                  />
                  1 to 10 of 11
                  <Button
                    onClick={() => previousPage()}
                    disabled={!canPreviousPage}
                    className="left"
                  >
                    {"<"}
                  </Button>
                  <Button
                    onClick={() => nextPage()}
                    disabled={!canNextPage}
                    className="right"
                  >
                    {">"}
                  </Button>
                </div>
              </div>
            </Grid>
          </Grid>
        </IsDraggingContext.Provider>
      </>
    )
  );
}
